import { erPatients, type ERPatient, type Triage } from './operations'

export type CommandSeverity = 'critical' | 'warning' | 'info' | 'success'

export interface EocPatient extends ERPatient {
  mrn: string
  visitNo: string
  arrivalMethod: 'Walk-in' | 'Ambulance' | 'Police' | 'Transfer'
  nurse: string
  losMin: number
  riskTags: string[]
  clinicalAlerts: string[]
  investigations: string
  medicationStatus: string
  consultation: string
  disposition: string
  earlyWarningScore: number
  deteriorationRisk: 'High' | 'Moderate' | 'Low'
  predictedBed: string
}

const patientMeta: Record<string, Omit<EocPatient, keyof ERPatient>> = {
  er1: { mrn: 'MRN-0051902', visitNo: 'ER-26-18431', arrivalMethod: 'Ambulance', nurse: 'N. Ahmed', losMin: 21, riskTags: ['Pediatric', 'Asthma'], clinicalAlerts: ['SpO₂ 89%', 'High EWS'], investigations: 'CXR ordered', medicationStatus: 'Nebulisation active', consultation: 'Paeds informed', disposition: 'Resus / review', earlyWarningScore: 8, deteriorationRisk: 'High', predictedBed: 'PICU in 34 min' },
  er2: { mrn: 'TMP-003391', visitNo: 'ER-26-18429', arrivalMethod: 'Police', nurse: 'S. Iqbal', losMin: 35, riskTags: ['Polytrauma', 'MLC', 'Blood required'], clinicalAlerts: ['MTP active', 'Unknown identity'], investigations: 'CT trauma protocol', medicationStatus: 'MTP 2 / 4', consultation: 'Trauma team bedside', disposition: 'OT decision pending', earlyWarningScore: 10, deteriorationRisk: 'High', predictedBed: 'Trauma ICU requested' },
  er3: { mrn: 'MRN-0049124', visitNo: 'ER-26-18436', arrivalMethod: 'Walk-in', nurse: 'M. Raza', losMin: 13, riskTags: ['STEMI pathway', 'Cardiac'], clinicalAlerts: ['ECG 4 min', 'Troponin pending'], investigations: 'Troponin 1 pending', medicationStatus: 'Aspirin given', consultation: 'Cardiology paged', disposition: 'Cath lab review', earlyWarningScore: 6, deteriorationRisk: 'High', predictedBed: 'CCU bed 1 available' },
  er4: { mrn: 'MRN-0050317', visitNo: 'ER-26-18433', arrivalMethod: 'Ambulance', nurse: 'H. Noor', losMin: 28, riskTags: ['Stroke', 'Thrombolysis window'], clinicalAlerts: ['CT due in 3 min'], investigations: 'CT head in progress', medicationStatus: 'NPO / IV access', consultation: 'Neurology bedside', disposition: 'Stroke unit', earlyWarningScore: 5, deteriorationRisk: 'High', predictedBed: 'Stroke unit in 18 min' },
  er5: { mrn: 'MRN-0056241', visitNo: 'ER-26-18418', arrivalMethod: 'Walk-in', nurse: 'A. Khan', losMin: 56, riskTags: ['Pain score 8/10'], clinicalAlerts: ['Waiting > 40 min'], investigations: 'CBC / US ordered', medicationStatus: 'Analgesia due', consultation: 'Unassigned', disposition: 'Physician assessment', earlyWarningScore: 3, deteriorationRisk: 'Moderate', predictedBed: 'Not anticipated' },
  er6: { mrn: 'MRN-0047762', visitNo: 'ER-26-18414', arrivalMethod: 'Walk-in', nurse: 'R. Malik', losMin: 70, riskTags: ['Fracture', 'Awaiting ortho'], clinicalAlerts: ['Boarding 22 min'], investigations: 'X-ray verified', medicationStatus: 'Splint applied', consultation: 'Ortho accepted', disposition: 'Admit pending', earlyWarningScore: 2, deteriorationRisk: 'Low', predictedBed: 'Surgical B in 42 min' },
  er7: { mrn: 'MRN-0056350', visitNo: 'ER-26-18439', arrivalMethod: 'Walk-in', nurse: 'F. Ali', losMin: 9, riskTags: ['Respiratory'], clinicalAlerts: [], investigations: 'None', medicationStatus: 'Not started', consultation: 'Waiting for clinician', disposition: 'Fast track', earlyWarningScore: 1, deteriorationRisk: 'Low', predictedBed: 'Not anticipated' },
  er8: { mrn: 'MRN-0057411', visitNo: 'ER-26-18442', arrivalMethod: 'Walk-in', nurse: 'F. Ali', losMin: 4, riskTags: ['Medication request'], clinicalAlerts: [], investigations: 'None', medicationStatus: 'Not started', consultation: 'Waiting for clinician', disposition: 'Fast track', earlyWarningScore: 0, deteriorationRisk: 'Low', predictedBed: 'Not anticipated' },
}

export const eocPatients: EocPatient[] = erPatients.map((patient) => ({ ...patient, ...patientMeta[patient.id] }))

export interface EocZone {
  name: string
  code: string
  capacity: number
  occupied: number
  critical: number
  transfer: number
  wait: string
  treatment: string
  staff: string
  status: CommandSeverity
}

export const eocZones: EocZone[] = [
  { name: 'Resuscitation', code: 'RES', capacity: 4, occupied: 3, critical: 3, transfer: 1, wait: '0 min', treatment: '37 min', staff: '2 MD · 5 RN', status: 'critical' },
  { name: 'Trauma Bay', code: 'TRM', capacity: 2, occupied: 2, critical: 2, transfer: 1, wait: '0 min', treatment: '48 min', staff: 'Trauma team', status: 'critical' },
  { name: 'Major Care', code: 'MAJ', capacity: 18, occupied: 15, critical: 4, transfer: 2, wait: '11 min', treatment: '76 min', staff: '5 MD · 10 RN', status: 'warning' },
  { name: 'Pediatric ED', code: 'PED', capacity: 8, occupied: 5, critical: 1, transfer: 0, wait: '7 min', treatment: '54 min', staff: '1 MD · 3 RN', status: 'success' },
  { name: 'Observation', code: 'OBS', capacity: 12, occupied: 10, critical: 0, transfer: 3, wait: '—', treatment: '3h 12m', staff: '1 MD · 4 RN', status: 'warning' },
  { name: 'Fast Track', code: 'FT', capacity: 10, occupied: 6, critical: 0, transfer: 0, wait: '18 min', treatment: '33 min', staff: '2 MD · 3 RN', status: 'info' },
]

export type EocKpiTone = 'blue' | 'red' | 'teal' | 'amber' | 'sky'

export interface EocKpi {
  label: string
  value: string
  detail: string
  trend: string
  tone: EocKpiTone
}

export const eocKpis: EocKpi[] = [
  { label: 'ER census', value: '41', detail: '74% of licensed capacity', trend: '+6 since 12:00', tone: 'blue' },
  { label: 'Immediate / emergent', value: '7', detail: '3 require senior review', trend: '2 new in last hour', tone: 'red' },
  { label: 'Door-to-doctor', value: '11m', detail: 'Target ≤ 15 min', trend: '3 min faster', tone: 'teal' },
  { label: 'Boarding', value: '6', detail: '2 exceed 90-minute target', trend: 'ICU constraint', tone: 'amber' },
  { label: '4-hour breaches', value: '3', detail: 'Forecast 5 by 18:00', trend: 'Escalation watch', tone: 'red' },
  { label: 'Incoming ambulances', value: '3', detail: 'Next arrival in 06 min', trend: '1 trauma alert', tone: 'sky' },
]

export interface CommandFeed {
  id: string
  title: string
  detail: string
  time: string
  source: string
  severity: CommandSeverity
}

export const eocCommandFeeds: CommandFeed[] = [
  { id: 'feed-1', title: 'Deterioration alert', detail: 'Bilal Ahmed Khan · SpO₂ 89% despite first nebulisation.', time: 'Now', source: 'Resus monitor', severity: 'critical' },
  { id: 'feed-2', title: 'Trauma activation', detail: 'Unknown male #3391 · MTP active; CT and OT on standby.', time: '3 min', source: 'Trauma bay', severity: 'critical' },
  { id: 'feed-3', title: 'ICU capacity constraint', detail: '1 staffed ICU bed; two emergency transfers expected within 45 minutes.', time: '6 min', source: 'Bed command', severity: 'warning' },
  { id: 'feed-4', title: 'Radiology queue breach', detail: '2 stat CT studies beyond the 20-minute reporting threshold.', time: '11 min', source: 'Radiology', severity: 'warning' },
  { id: 'feed-5', title: 'Blood bank ready', detail: 'O negative emergency pack replenished; 12 units immediately available.', time: '14 min', source: 'Blood bank', severity: 'success' },
]

export const eocCapacity = [
  { label: 'ICU staffed beds', value: '1 / 20', note: '2 expected releases', status: 'critical' as const },
  { label: 'HDU beds', value: '3 / 12', note: 'All ready now', status: 'success' as const },
  { label: 'Ward beds', value: '9 / 86', note: '3 cleaning in progress', status: 'warning' as const },
  { label: 'Operating theatres', value: '6 / 8', note: '1 trauma room available', status: 'info' as const },
]

export const eocArrivalTrend = [18, 22, 29, 24, 35, 39, 31, 27, 34, 42, 37, 30]

export const eocQualityMetrics = [
  { label: 'Door-to-ECG', value: '7m', target: '≤ 10m', status: 'success' as const },
  { label: 'Door-to-CT stroke', value: '16m', target: '≤ 20m', status: 'success' as const },
  { label: 'Sepsis bundle', value: '91%', target: '≥ 90%', status: 'success' as const },
  { label: 'LWBS today', value: '1.8%', target: '< 2%', status: 'warning' as const },
]

export const triageLabels: Record<Triage, string> = {
  'ESI-1': 'Immediate',
  'ESI-2': 'Emergent',
  'ESI-3': 'Urgent',
  'ESI-4': 'Less urgent',
  'ESI-5': 'Non-urgent',
}
